import { BorderInput } from "@/comps/border-input"
import Button from "@/comps/button"
import { Logo } from "@/comps/header/icon"
import { CustomThemedResource } from "@/comps/image"
import { useValidator } from "@/comps/input"
import { ErrorMessage } from "@/comps/message"
import { useAdjustLogger } from "@/utils/adjust"
import { axios } from "@/utils/axios-wrapper"
import { assetUrl } from "@/utils/cdn"
import useAuth, {
	logInWithUserCredential,
	useAuthUpdater,
} from "@/utils/client-auth"
import {
	LoginPage,
	loginPageSchema,
} from "@/utils/login-redirect"
import { NotificationContext } from "@/utils/notification"
import {
	SSRTemporaryRedirect,
	wrapSSRAuth,
} from "@/utils/ssr-auth"
import { isEmail, notEmpty } from "@/utils/validators"
import clsx from "clsx"
import crypto from "crypto"
import { FirebaseError } from "firebase/app"
import {
	AuthProvider,
	getAuth,
	getRedirectResult,
	GoogleAuthProvider,
	OAuthProvider,
} from "firebase/auth"
import { VerifyEmailRes } from "models/general"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import {
	FormEvent,
	ReactNode,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react"
import { z } from "zod"
import { Meta } from "../comps"

const PROVIDER_SIGNIN_KEY = "googleSignInInitiated"

interface LoginProps {
	redirect_url: string | string[] | null
	type: LoginPage
	expired: boolean
}

type MetaTitleTypes = "txt_login_title" | "txt_signup_title"

export default function Login(props: LoginProps) {
	const { redirect_url, type, expired } = props

	const [page, setPage] = useState<LoginPage>(type)
	const [email, setEmail] = useState("")
	const [newPassword, setNewPassword] = useState("")
	// const [quiz, setQuiz] = useState<Quiz | null>(null)
	const [requested, setRequested] = useState(false)
	const [metaTitle, setMetaTitle] =
		useState<MetaTitleTypes>(
			type === "signup"
				? "txt_signup_title"
				: "txt_login_title",
		)

	const { t } = useTranslation()
	const { userInfo, setUserInfo } = useAuth()
	const router = useRouter()
	const logAdjust = useAdjustLogger()

	useEffect(() => {
		const handleRedirectResult = async () => {
			const signInInitiated = localStorage.getItem(
				PROVIDER_SIGNIN_KEY,
			)

			if (!signInInitiated) {
				return
			}
			setRequested(true)

			const auth = getAuth()
			let attempt = 0
			const maxAttempts = 3

			try {
				let result = await getRedirectResult(auth)

				while (!result && attempt < maxAttempts) {
					await new Promise((resolve) =>
						setTimeout(resolve, 500),
					)
					result = await getRedirectResult(auth)
					attempt++
				}

				if (result) {
					const newUserInfo = await logInWithUserCredential(
						result,
					)
					setUserInfo(newUserInfo)

					let url = "/"

					if (typeof redirect_url === "string") {
						url = redirect_url
					}

					const hashEmail = (email: string | null) => {
						if (!email) return null
						return crypto
							.createHash("sha256")
							.update(email)
							.digest("hex")
					}

					if (type === "signup") {
						const hashedEmail = hashEmail(result.user.email)
						logAdjust?.logEventWithPayload(
							"w_did_sign_up",
							{
								from: url,
								email: hashedEmail,
							},
						)
					} else if (type === "signin") {
						logAdjust?.logEventWithPayload(
							"w_did_sign_in",
							{
								from: url,
							},
						)
					}

					await router.push(url)
					setEmail("")
					setRequested(false)
					localStorage.removeItem(PROVIDER_SIGNIN_KEY)
				} else if (!result && !userInfo) {
					setRequested(false)
					localStorage.removeItem(PROVIDER_SIGNIN_KEY)
				}
			} catch (error) {
				console.error(
					"Error handling redirect result",
					error,
				)
				if (!userInfo) {
					setRequested(false)
				}
				localStorage.removeItem(PROVIDER_SIGNIN_KEY)
			}
		}

		if (router.isReady) {
			handleRedirectResult()
		}
	}, [
		logAdjust,
		redirect_url,
		router,
		setUserInfo,
		type,
		userInfo,
	])

	return (
		<>
			<Meta
				title={t(metaTitle)}
				description={t("txt_login_description")}
			/>
			<div className="flex min-h-[100svh] w-screen flex-row overflow-hidden">
				{/* Left Panel */}
				<LeftPanel />

				{/* Right Panel */}
				<div className="relative z-[2] flex min-h-[100svh] w-full flex-col items-center justify-start gap-1 overflow-hidden bg-color-surface-cell tablet:justify-between desktop:h-auto desktop:min-h-screen desktop:w-[50%] desktop:overflow-visible">
					<div className="hidden h-4 w-full tablet:block" />

					<div className="relative flex w-full flex-1 items-center justify-start tablet:justify-center">
						<SignInSection
							page={page}
							email={email}
							setEmail={setEmail}
							setPage={setPage}
							redirectUrl={redirect_url}
							setMetaTitle={setMetaTitle}
							requested={requested}
							setRequested={setRequested}
						/>
						<SignUpSection
							page={page}
							email={email}
							setEmail={setEmail}
							setPage={setPage}
							redirectUrl={redirect_url}
							newPassword={newPassword}
							setNewPassword={setNewPassword}
							setMetaTitle={setMetaTitle}
							requested={requested}
							setRequested={setRequested}
						/>
						<VerifySection
							page={page}
							email={email}
							setEmail={setEmail}
							setPage={setPage}
							redirectUrl={redirect_url}
							newPassword={newPassword}
							setNewPassword={setNewPassword}
							setMetaTitle={setMetaTitle}
							requested={requested}
							setRequested={setRequested}
						/>
						{/* <UsernameSection
							page={page}
							email={email}
							setEmail={setEmail}
							setPage={setPage}
							redirectUrl={redirect_url}
							newPassword={newPassword}
							setNewPassword={setNewPassword}
							setQuiz={setQuiz}
							setMetaTitle={setMetaTitle}
							requested={requested}
							setRequested={setRequested}
						/> */}
						{/* {quiz && (
							<QuizSection
								page={page}
								quiz={quiz}
								setPage={setPage}
								requested={requested}
								setRequested={setRequested}
							/>
						)} */}
						{/* <PlanSection
							setPage={setPage}
							page={page}
							redirectUrl={redirect_url}
							setMetaTitle={setMetaTitle}
						/> */}
						{/* <GenerateSection
              page={page}
              redirectUrl={redirect_url}
              setPage={setPage}
            /> */}
						<ForgotSection
							page={page}
							email={email}
							setEmail={setEmail}
							setPage={setPage}
							redirectUrl={redirect_url}
							expired={expired}
							setMetaTitle={setMetaTitle}
							requested={requested}
							setRequested={setRequested}
						/>
						<SuccessSection page={page} />
					</div>

					{(page === "signin" || page === "signup") && (
						<div className="hidden tablet:block">
							<div className="h-4 w-full"></div>
							<span
								className={clsx(
									"hidden max-w-full self-center pb-7 text-center text-[15px] transition-opacity tablet:block",
								)}>
								<span>
									{t("txt_by_signing_up_you_accept")}
								</span>
								<a
									href="/privacy-policy"
									className="mx-1 text-[#007BFF]">
									{t("txt_terms_of_use")}
								</a>
								<span>{t("lbl_and")}</span>
								<a
									href="/privacy-policy"
									className="mx-1 text-[#007BFF]">
									{t("txt_privacy_policy")}
								</a>
							</span>
						</div>
					)}
				</div>
			</div>
		</>
	)
}

export function LeftPanel() {
	const { t } = useTranslation()

	return (
		<div className="relative z-[1] hidden min-h-screen desktop:block desktop:w-[50%]">
			<div className="pointer-events-none absolute left-0 top-0 h-full w-full select-none">
				<CustomThemedResource
					source="/general/login-bg"
					format="webp"
					alt="login left panel background image"
					className="h-full w-full object-cover"
				/>
			</div>

			<div className="absolute left-1/2 top-[50px] z-10 -translate-x-1/2">
				<a href="/">
					<CustomThemedResource
						source="/logos/logo"
						format="svg"
						alt="Vidsup main logo"
						className="h-[45px] w-[131px]"
					/>
				</a>
			</div>
			<div className="relative flex h-full w-full flex-col items-center justify-center pt-[20px]">
				<div
					style={{
						borderRadius: "20px",
						backdropFilter: "blur(1px)",
					}}
					className={clsx(
						"bg-gradient-to-b p-[64px] pb-[30px]",
						"flex flex-col items-center justify-center",
						"from-[#F7FAFD99] to-[#EAF1F999]",
						"dark:from-[#1F252C99] dark:to-[#1F252C99]",
					)}>
					<p className="pointer-events-none select-none text-center text-[22px] font-500 text-blue-800">
						{t("txt_turn_your_idea")}
					</p>
					<CustomThemedResource
						source="/login/video-cover"
						format="webp"
						alt="login left panel video cover image"
						className="pointer-events-none mt-9 w-[312px] select-none object-cover"
					/>
				</div>
			</div>
		</div>
	)
}

const sectionOrder = {
	plan: 0,
	quiz: 1,
	username: 2,
	verify: 3,
	signup: 4,
	signin: 5,
	forgot: 6,
	success: 7,
} as const

export function handleStopScroll(event: Event) {
	event.stopPropagation()
	event.preventDefault()
}

const googleLogo = (
	<svg
		width="25"
		height="24"
		viewBox="0 0 25 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M5.85707 11.9992C5.85707 11.2977 5.97351 10.6251 6.18158 9.99433L2.54164 7.21484C1.83222 8.65513 1.43262 10.2782 1.43262 11.9992C1.43262 13.719 1.83188 15.3408 2.54012 16.7803L6.17804 13.9954C5.97199 13.3675 5.85707 12.6974 5.85707 11.9992Z"
			fill="#FBBC05"
		/>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M12.2483 5.61743C13.7723 5.61743 15.1488 6.15743 16.2303 7.04101L19.3765 3.89922C17.4593 2.23011 15.0013 1.19922 12.2483 1.19922C7.97422 1.19922 4.30087 3.64339 2.54199 7.21482L6.18176 9.9943C7.02045 7.44854 9.41113 5.61743 12.2483 5.61743Z"
			fill="#EA4335"
		/>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M12.2483 18.3808C9.4113 18.3808 7.02062 16.5497 6.18193 14.0039L2.54199 16.7829C4.30087 20.3548 7.97422 22.799 12.2483 22.799C14.8862 22.799 17.4048 21.8623 19.295 20.1073L15.84 17.4363C14.8651 18.0504 13.6375 18.3808 12.2483 18.3808Z"
			fill="#34A853"
		/>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M22.5717 11.9987C22.5717 11.3605 22.4733 10.6732 22.3258 10.0352H12.248V14.2078H18.049C17.7589 15.6306 16.9695 16.7242 15.8397 17.436L19.2947 20.107C21.2802 18.2643 22.5717 15.519 22.5717 11.9987Z"
			fill="#4285F4"
		/>
	</svg>
)

const appleLogo = (
	<svg
		width="25"
		height="24"
		viewBox="0 0 25 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg">
		<path
			d="M18.6661 12.13C18.6368 9.23826 21.0626 7.85131 21.171 7.78124C19.8077 5.81983 17.6838 5.5507 16.9279 5.52008C15.1218 5.33957 13.402 6.5669 12.4857 6.5669C11.5714 6.5669 10.1557 5.54652 8.65806 5.57251C6.68786 5.60128 4.87187 6.70008 3.858 8.43551C1.81189 11.9282 3.33457 17.1043 5.32834 19.939C6.30306 21.3246 7.46547 22.8837 8.99051 22.8266C10.4604 22.7695 11.0149 21.8911 12.7918 21.8911C14.5686 21.8911 15.0676 22.8266 16.6223 22.7974C18.203 22.7691 19.2051 21.3854 20.1718 19.9942C21.2908 18.3845 21.7515 16.8259 21.7789 16.747C21.7444 16.7312 18.6977 15.5828 18.6661 12.13Z"
			fill="var(--apple-color)"
		/>
		<path
			d="M15.7447 3.64487C16.5534 2.67878 17.1014 1.33638 16.9524 0C15.7853 0.0464019 14.3725 0.763312 13.5345 1.72847C12.7838 2.58505 12.1273 3.9488 12.3032 5.26105C13.6052 5.36035 14.9331 4.6091 15.7447 3.64487Z"
			fill="var(--apple-color)"
		/>
	</svg>
)

interface SignInProvider {
	name: string
	icon: ReactNode
	provider: () => AuthProvider
}

const authProviders: SignInProvider[] = [
	{
		name: "Google",
		icon: googleLogo,
		provider: () => {
			const provider = new GoogleAuthProvider()
			provider.addScope("email")

			return provider
		},
	},
	{
		name: "Apple",
		icon: appleLogo,
		provider: () => {
			const provider = new OAuthProvider("apple.com")
			provider.addScope("email")

			return provider
		},
	},
]

export interface SignInSignUpSectionProps {
	redirectUrl: string | string[] | null
	page: LoginPage
	setPage: (page: LoginPage) => void
	email: string
	setEmail: (email: string) => void
	expired?: boolean
	// setQuiz: (quiz: Quiz) => void
	setMetaTitle: (meta: MetaTitleTypes) => void
	requested: boolean
	setRequested: (v: boolean) => void
}

export interface SignUpSectionProps {
	redirectUrl: string | string[] | null
	page: LoginPage
	setPage: (page: LoginPage) => void
	email: string
	setEmail: (email: string) => void
	newPassword: string
	setNewPassword: (password: string) => void
	// setQuiz: (quiz: Quiz) => void
	setMetaTitle: (meta: MetaTitleTypes) => void
	requested: boolean
	setRequested: (v: boolean) => void
}

export function SuccessSection({
	page,
}: {
	page: LoginPage
}) {
	const { t } = useTranslation()

	return (
		<div
			className={clsx(
				"absolute right-0 top-0 tablet:right-[calc(50%-185px)]",
				"h-full w-full tablet:w-[375px]",
				"flex items-center justify-center",
				"transition-all duration-500",
				page === "success"
					? ["opacity-100 delay-200", "translate-y-0"]
					: [
							"pointer-events-none opacity-0",
							sectionOrder[page] > sectionOrder.success
								? "translate-y-24"
								: "-translate-y-24",
					  ],
			)}>
			<div className="flex flex-col items-center gap-3 text-[14px] font-500">
				<img
					src={assetUrl("/general/success-check.svg")}
					alt="success-check"
					className="aspect-square w-[60px]"
				/>
				<div className="px-4 text-center">
					<p>{t("txt_please_check_email")}</p>
				</div>
			</div>
		</div>
	)
}

export function ForgotSection(
	props: SignInSignUpSectionProps,
) {
	const {
		page,
		email,
		setEmail,
		setPage,
		expired,
		requested,
		setRequested,
	} = props

	const { t } = useTranslation()
	const logAdjust = useAdjustLogger()

	const emailValidator = useValidator(email, [
		notEmpty,
		isEmail,
	])

	const notCurrent = page !== "forgot"

	const { notify } = useContext(NotificationContext)
	const { sendPasswordResetEmail } = useAuthUpdater()

	async function handleReset(
		event: FormEvent<HTMLFormElement>,
	) {
		event.preventDefault()
		setRequested(true)

		try {
			await sendPasswordResetEmail(email)
			logAdjust?.logEvent("w_dp_send_password_reset_link")

			setPage("success")
			setEmail("")
		} catch (error) {
			console.error(error)

			if (error instanceof FirebaseError) {
				switch (error.code) {
					case "auth/user-not-found":
						notify(
							<ErrorMessage>
								{t("txt_user_not_found")}
							</ErrorMessage>,
						)
						break

					default:
						notify(
							<ErrorMessage>
								{t("txt_unexpected_error")}
							</ErrorMessage>,
						)
				}
			} else {
				notify(
					<ErrorMessage>
						{t("txt_unexpected_error")}
					</ErrorMessage>,
				)
			}
		}

		setRequested(false)
	}

	return (
		<form
			className={clsx(
				"absolute right-0 top-0 tablet:right-[calc(50%-185px)]",
				"h-full w-full tablet:w-[375px]",
				"flex items-center justify-center",
				"transition-all duration-500",
				page === "forgot"
					? ["opacity-100 delay-200", "translate-y-0"]
					: [
							"pointer-events-none opacity-0",
							sectionOrder[page] > sectionOrder.forgot
								? "translate-y-24"
								: "-translate-y-24",
					  ],
			)}
			onSubmit={handleReset}>
			<div className="flex w-full flex-col items-stretch gap-3 px-4 tablet:relative tablet:w-[375px] tablet:px-0">
				<h3 className="mb-4 text-center text-[20px] font-700 text-blue-800">
					{t("txt_forgot_password")}
				</h3>
				<button
					type="button"
					className={clsx(
						"absolute left-2 top-2 tablet:left-0 tablet:top-0",
						"bg-blue-200 hover:bg-blue-100",
						"transition-colors duration-200",
						"h-8 w-8 rounded-full px-3",
					)}
					onClick={() => setPage("signin")}>
					<svg
						width="6"
						height="12"
						viewBox="0 0 6 12"
						fill="none"
						xmlns="http://www.w3.org/2000/svg">
						<path
							d="M5.12969 10.2594L0.871094 6.00078L5.12969 1.74219"
							stroke="var(--color-blue-900)"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</button>

				<div className="flex flex-col items-stretch">
					<BorderInput
						name="email"
						type="email"
						placeholder={t("lbl_email")}
						className="!bg-color-background"
						value={email}
						disabled={requested || notCurrent}
						onChange={function (event) {
							setEmail(event.currentTarget.value)
						}}
						onBlur={emailValidator.validate}
					/>
				</div>

				<Button
					type="submit"
					className="mt-4 w-full"
					text="common:txt_send_password_reset"
					loading={requested}
					disabled={requested || notCurrent}
					primary
				/>

				<div className="flex flex-col items-center gap-1">
					<span
						className={clsx(
							"text-[13px] font-400",
							expired
								? "text-color-error"
								: "text-blue-700",
						)}>
						{expired
							? t("txt_link_expired")
							: t("txt_we_will_email")}
					</span>
					<strong className="block h-8 w-full truncate text-center">
						{email}
					</strong>
				</div>
			</div>
		</form>
	)
}

function getErrorName(error: unknown): string {
	if (error instanceof FirebaseError) {
		switch (error.code) {
			case "auth/wrong-password":
				return "txt_wrong_password"
			case "auth/user-not-found":
				return "txt_user_not_found"
			case "auth/too-many-requests":
				return "txt_too_many_requests"
		}
	}
	return "txt_unknown_error"
}

export function SignInSection(
	props: SignInSignUpSectionProps,
) {
	const {
		page,
		email,
		setEmail,
		setPage,
		redirectUrl,
		// setQuiz,
		setMetaTitle,
		requested,
	} = props

	const { t } = useTranslation()
	const router = useRouter()
	const logAdjust = useAdjustLogger()

	const emailValidator = useValidator(email, [
		notEmpty,
		isEmail,
	])
	const [password, setPassword] = useState("")
	const [passwordHidden, setPasswordHidden] = useState(true)
	const [loading, setLoading] = useState(false)

	const notCurrent = page !== "signin"
	const {
		logInWithEmailAndPassword: signInWithEmailAndPassword,
	} = useAuthUpdater()

	async function handleSignIn(
		event: FormEvent<HTMLFormElement>,
	) {
		event.preventDefault()
		if (
			email.trim().length === 0 ||
			password.trim().length === 0
		)
			return
		setLoading(true)

		logAdjust?.logEventWithPayload("w_dp_sign_in", {
			from:
				typeof redirectUrl === "string"
					? redirectUrl
					: "home",
			type: "email",
		})

		try {
			await signInWithEmailAndPassword(email, password)
			// if (userInfo.details.promptOnboarding) {
			// 	setPage("username")
			// 	setRequested(false)
			// 	return
			// }
			// } else {
			// 	const quizParams = {
			// 		uid: userInfo.userId,
			// 	}
			// 	const quiz = await axios
			// 		.post<Quiz | null>("/api/quiz", quizParams)
			// 		.then((res) => res.data)
			// 	if (quiz) {
			// 		setQuiz(quiz)
			// 		setPage("quiz")
			// 		setRequested(false)
			// 		return
			// 	}
			// }
			let url = "/"

			if (typeof redirectUrl === "string") {
				url = redirectUrl
			}

			logAdjust?.logEventWithPayload("w_did_sign_in", {
				from: url,
			})
			await router.push(url)

			setEmail("")
			setPassword("")
			setPasswordHidden(true)
		} catch (error) {
			console.error(error)
			const errorText = getErrorName(error)
			notify(<ErrorMessage>{t(errorText)}</ErrorMessage>)
		}

		setLoading(false)
	}

	const { notify } = useContext(NotificationContext)
	const { logInWithProvider } = useAuthUpdater()

	async function handleProviderSignIn(
		provider: AuthProvider,
		name: string,
	) {
		setLoading(true)

		logAdjust?.logEventWithPayload("w_dp_sign_in", {
			from:
				typeof redirectUrl === "string"
					? redirectUrl
					: "home",
			type: name,
		})

		try {
			localStorage.setItem(PROVIDER_SIGNIN_KEY, "true")
			logInWithProvider(provider)
			// await logInWithProvider(provider)

			// if (userInfo.details.promptOnboarding) {
			// 	setPage("username")
			// 	setRequested(false)
			// 	return
			// } else {
			// 	const quizParams = {
			// 		uid: userInfo.userId,
			// 	}
			// 	const quiz = await axios
			// 		.post<Quiz | null>("/api/quiz", quizParams)
			// 		.then((res) => res.data)
			// 	if (quiz) {
			// 		setQuiz(quiz)
			// 		setPage("quiz")
			// 		setRequested(false)
			// 		return
			// 	}
			// }
			// let url = "/"

			// if (typeof redirectUrl === "string") {
			// 	url = redirectUrl
			// }

			// logAdjust?.logEventWithPayload("w_did_sign_in", {
			// 	from: url,
			// })

			// await router.push(url)

			// setEmail("")
			// setPassword("")
			// setPasswordHidden(true)
		} catch (error) {
			console.error(error)
			const errorText = getErrorName(error)
			notify(<ErrorMessage>{t(errorText)}</ErrorMessage>)
		}
		setLoading(false)
	}

	return (
		<form
			className={clsx(
				"absolute right-0 top-0 tablet:right-[calc(50%-185px)]",
				"h-full w-full tablet:w-[375px]",
				"flex justify-center tablet:items-center tablet:pt-0",
				"pb-[60px] transition-all duration-500 tablet:pb-0",
				page === "signin"
					? ["opacity-100 delay-200", "translate-y-0"]
					: [
							"pointer-events-none opacity-0",
							sectionOrder[page] > sectionOrder.signin
								? "translate-y-24"
								: "-translate-y-24",
					  ],
			)}
			onSubmit={handleSignIn}>
			<div className="no-scrollbar flex w-full flex-col items-stretch gap-3 overflow-auto  px-4 pt-8 tablet:w-[375px] tablet:px-0 tablet:pt-0">
				<div className="flex w-full items-center justify-center desktop:hidden">
					<Logo showOnMobile />
				</div>
				<h3 className="mb-4 text-center text-[20px] font-700 text-blue-800">
					{t("txt_sign_in_vidsup")}
				</h3>

				<div className="flex flex-col gap-2 [--apple-color:#121212] [--facebook-color:#345287] dark:[--apple-color:#ffffff] dark:[--facebook-color:#ffffff]">
					{authProviders.map((authProvider, index) => (
						<button
							key={index}
							type="button"
							className={clsx(
								"relative h-[44px] w-full shrink-0 gap-3 rounded-lg font-500 tablet:h-[50px]",
								"bg-blue-100 hover:bg-blue-200 disabled:hover:bg-blue-100",
								"transition-colors duration-300 ease-in-out",
								"flex items-center justify-center",
							)}
							onClick={() =>
								handleProviderSignIn(
									authProvider.provider(),
									authProvider.name,
								)
							}
							disabled={notCurrent || requested}>
							<div className="absolute left-3 top-[9px] tablet:left-[15px] tablet:top-[12px]">
								{authProvider.icon}
							</div>
							<p className=" text-[15px] font-500 text-blue-700">
								{t("fs_continue_with", {
									provider: authProvider.name,
								})}
							</p>
						</button>
					))}
				</div>

				<div className="flex items-center gap-2">
					<div className="h-[1px] flex-1 bg-color-separator" />
					<span className="select-none text-[13px] font-400 text-blue-700">
						{t("lbl_or")}
					</span>
					<div className="h-[1px] flex-1 bg-color-separator" />
				</div>

				<div className="flex flex-col items-stretch">
					<BorderInput
						name="email"
						type="email"
						value={email}
						placeholder={t("lbl_email")}
						disabled={requested || notCurrent}
						className=" !h-11 !bg-color-background tablet:!h-[50px]"
						onChange={function (event) {
							setEmail(event.currentTarget.value)
						}}
						onBlur={emailValidator.validate}
					/>
				</div>

				<div className="relative flex flex-col items-stretch overflow-visible">
					<div className="relative">
						<BorderInput
							name="password"
							placeholder={t("lbl_password")}
							type={passwordHidden ? "password" : "text"}
							disabled={requested || notCurrent}
							className="peer !h-11 w-full !bg-color-background pr-12 tablet:!h-[50px]"
							onChange={(event) =>
								setPassword(event.currentTarget.value)
							}
						/>

						<button
							type="button"
							disabled={
								passwordHidden ||
								notCurrent ||
								password.length === 0
							}
							onClick={() => setPasswordHidden(true)}
							className={clsx(
								"absolute right-2 top-1/2 -translate-y-1/2 p-2",
								"transition-opacity duration-150",
								passwordHidden &&
									"pointer-events-none opacity-0",
								password.length === 0 &&
									"pointer-events-none opacity-0",
							)}>
							<img
								src={assetUrl(
									"/general/eye-icon-closed.svg",
								)}
								alt="show password"
							/>
						</button>

						<button
							type="button"
							disabled={
								!passwordHidden ||
								notCurrent ||
								password.length === 0
							}
							onClick={() => setPasswordHidden(false)}
							className={clsx(
								"absolute right-2 top-1/2 -translate-y-1/2 p-2",
								"transition-opacity duration-150",
								!passwordHidden &&
									"pointer-events-none opacity-0",
								password.length === 0 &&
									"pointer-events-none opacity-0",
							)}>
							<img
								src={assetUrl("/general/eye-icon.svg")}
								alt="hide password"
							/>
						</button>
					</div>
				</div>

				<button
					type="button"
					className={clsx(
						"self-end text-[14px] font-400 text-blue-500 tablet:self-center",
						"underline decoration-blue-500 hover:!decoration-blue-500 tablet:decoration-[transparent]",
						"transition-colors duration-200 ease-in-out",
						"cursor-pointer",
					)}
					onClick={() => {
						logAdjust?.logEvent("w_dp_forgot_password")
						setPage("forgot")
					}}>
					{t("txt_dont_remember_password")}
				</button>

				<Button
					type="submit"
					className="w-full tablet:mt-2"
					text="lbl_signin"
					loading={requested || loading}
					disabled={requested || notCurrent || loading}
					primary
				/>

				<span className="max-w-full self-center pb-4 text-center text-[12px] tablet:hidden tablet:pb-0">
					<span>{t("txt_by_signing_up_you_accept")}</span>
					<a
						href="/privacy-policy"
						className="mx-1 text-[#007BFF]">
						{t("txt_terms_of_use")}
					</a>
					<span>{t("lbl_and")}</span>
					<a
						href="/privacy-policy"
						className="mx-1 text-[#007BFF]">
						{t("txt_privacy_policy")}
					</a>
				</span>

				<div
					className={clsx(
						"fixed bottom-0 left-0 z-[7] w-full bg-color-cell tablet:relative tablet:bg-[transparent]",
						"border-t border-color-separator tablet:border-[transparent]",
						"flex justify-center",
					)}>
					<button
						type="button"
						className="h-[60px] text-[15px] tablet:h-auto"
						onClick={() => {
							setPage("signup")
							setMetaTitle("txt_signup_title")
						}}>
						{t("txt_dont_have_account")}
						<b className="ml-1 font-700 text-primary-500">
							{t("lbl_signup")}
						</b>
					</button>
				</div>
			</div>
		</form>
	)
}

export function SignUpSection(props: SignUpSectionProps) {
	const {
		page,
		email,
		setEmail,
		setPage,
		newPassword: password,
		setNewPassword: setPassword,
		redirectUrl,
		setMetaTitle,
		requested,
		setRequested,
	} = props

	const { t } = useTranslation()
	const logAdjust = useAdjustLogger()

	const notCurrent = page !== "signup"

	const emailValidator = useValidator(email, [
		notEmpty,
		isEmail,
	])
	const [confirm, setConfirm] = useState("")
	const [passwordHidden, setPasswordHidden] = useState(true)
	const [confirmHidden, setConfirmHidden] = useState(true)

	const { notify } = useContext(NotificationContext)
	const { logInWithProvider: signInWithPopup } =
		useAuthUpdater()

	// async function checkEmail(
	// 	event: FormEvent<HTMLFormElement>,
	// ) {
	// 	event.preventDefault()

	// 	if (
	// 		email.trim().length === 0 ||
	// 		password.trim().length === 0
	// 	)
	// 		return

	// 	setRequested(true)

	// 	logAdjust?.logEventWithPayload("w_dp_sign_up", {
	// 		from:
	// 			typeof redirectUrl === "string"
	// 				? redirectUrl
	// 				: "home",
	// 		type: "email",
	// 	})

	// 	try {
	// 		const canContinue = await axios
	// 			.post<VerifyEmailRes>("/api/verify-email", {
	// 				email,
	// 			})
	// 			.then((res) => res.data)

	// 		if (canContinue.status === true) {
	// 			setPage("verify")

	// 			setPasswordHidden(true)
	// 			setConfirm("")
	// 			setConfirmHidden(true)
	// 			setRequested(false)
	// 			return
	// 		} else {
	// 			switch (canContinue.code) {
	// 				case "already_linked":
	// 					notify(
	// 						<ErrorMessage>
	// 							{t("txt_email_already_in_use")}
	// 						</ErrorMessage>,
	// 					)
	// 					break
	// 				case "invalid_email":
	// 					notify(
	// 						<ErrorMessage>
	// 							{t("txt_email_not_valid")}
	// 						</ErrorMessage>,
	// 					)
	// 					break
	// 				default:
	// 					notify(
	// 						<ErrorMessage>
	// 							{t("txt_email_already_in_use")}
	// 						</ErrorMessage>,
	// 					)
	// 					break
	// 			}
	// 			setRequested(false)
	// 		}
	// 	} catch (error) {
	// 		console.error(error)
	// 		notify(
	// 			<ErrorMessage>
	// 				{t("txt_couldnt_create_account")}
	// 			</ErrorMessage>,
	// 		)
	// 	}

	// 	setRequested(false)
	// }

	async function checkEmail(
		event: FormEvent<HTMLFormElement>,
	) {
		event.preventDefault()

		if (
			email.trim().length === 0 ||
			password.trim().length === 0
		)
			return

		setRequested(true)

		logAdjust?.logEventWithPayload("w_dp_sign_up", {
			from:
				typeof redirectUrl === "string"
					? redirectUrl
					: "home",
			type: "email",
		})

		try {
			const canContinue = await axios
				.post<VerifyEmailRes>("/api/v1/auth/verify/email", {
					email,
				})
				.then((res) => res.data)

			if (canContinue.status === true) {
				setPage("verify")

				setPasswordHidden(true)
				setConfirm("")
				setConfirmHidden(true)
				setRequested(false)
				return
			} else {
				switch (canContinue.code) {
					case "already_linked":
						notify(
							<ErrorMessage>
								{t("txt_email_already_in_use")}
							</ErrorMessage>,
						)
						break
					case "invalid_email":
						notify(
							<ErrorMessage>
								{t("txt_email_not_valid")}
							</ErrorMessage>,
						)
						break
					default:
						notify(
							<ErrorMessage>
								{t("txt_email_already_in_use")}
							</ErrorMessage>,
						)
						break
				}
				setRequested(false)
			}
		} catch (error) {
			console.error(error)
			notify(
				<ErrorMessage>
					{t("txt_couldnt_create_account")}
				</ErrorMessage>,
			)
		}

		setRequested(false)
	}

	async function handleProviderSignIn(
		provider: AuthProvider,
		name: string,
	) {
		setRequested(true)
		logAdjust?.logEventWithPayload("w_dp_sign_up", {
			from:
				typeof redirectUrl === "string"
					? redirectUrl
					: "home",
			type: name,
		})
		try {
			localStorage.setItem(PROVIDER_SIGNIN_KEY, "true")
			signInWithPopup(provider)

			// if (userInfo.details.promptOnboarding) {
			// 	setPage("username")
			// 	setRequested(false)
			// 	return
			// } else {
			// 	const quizParams = {
			// 		uid: userInfo.userId,
			// 	}
			// 	const quiz = await axios
			// 		.post<Quiz | null>("/api/quiz", quizParams)
			// 		.then((res) => res.data)
			// 	if (quiz) {
			// 		setQuiz(quiz)
			// 		setPage("quiz")
			// 		setRequested(false)
			// 		return
			// 	}
			// }
			// let url = "/"

			// if (typeof redirectUrl === "string") {
			// 	url = redirectUrl
			// }

			// const hashEmail = (email: string | null) => {
			// 	if (!email) return null
			// 	return crypto
			// 		.createHash("sha256")
			// 		.update(email)
			// 		.digest("hex")
			// }

			// const hashedEmail = hashEmail(email)

			// logAdjust?.logEventWithPayload("w_did_sign_up", {
			// 	from: url,
			// 	email: hashedEmail,
			// })
			// await router.push(url)

			// setEmail("")
			// setPassword("")
		} catch (error) {
			console.error(error)
			notify(
				<ErrorMessage>
					{t("txt_couldnt_create_account")}
				</ErrorMessage>,
			)
		}
		setRequested(false)
	}

	const notSamePassword = password !== confirm

	return (
		<form
			className={clsx(
				"absolute right-0 top-0 tablet:right-[calc(50%-185px)]",
				"h-full w-full tablet:w-[375px]",
				"flex flex-col justify-start tablet:items-center tablet:justify-center tablet:pt-0",
				"pb-[60px] transition-all duration-500 tablet:pb-0",
				page === "signup"
					? ["opacity-100 delay-200", "translate-y-0"]
					: [
							"pointer-events-none opacity-0",
							sectionOrder[page] > sectionOrder.signup
								? "translate-y-24"
								: "-translate-y-24",
					  ],
			)}
			onSubmit={checkEmail}>
			<div className="no-scrollbar flex h-full w-full flex-col items-stretch gap-3 overflow-auto px-4 pt-8 tablet:h-auto tablet:w-[375px] tablet:overflow-visible tablet:px-0 tablet:pt-0">
				<div className="flex w-full items-center justify-center desktop:hidden">
					<Logo showOnMobile />
				</div>
				<div className="flex w-full items-center justify-center">
					<h3
						className="mb-4 w-[280px] text-center text-[20px] font-700 text-blue-800"
						id="signup-title">
						{t("txt_you_need_account")}
					</h3>
				</div>

				<div className="flex flex-col gap-2 [--apple-color:#121212] [--facebook-color:#345287] dark:[--apple-color:#ffffff] dark:[--facebook-color:#ffffff]">
					{authProviders.map((authProvider, index) => (
						<button
							key={index}
							type="button"
							className={clsx(
								"relative h-[44px] w-full shrink-0 gap-3 rounded-lg font-500 tablet:h-[50px]",
								"bg-blue-100 hover:bg-blue-200 disabled:hover:bg-blue-100",
								"transition-colors duration-300 ease-in-out",
								"flex items-center justify-center",
							)}
							onClick={() =>
								handleProviderSignIn(
									authProvider.provider(),
									authProvider.name,
								)
							}
							disabled={notCurrent || requested}>
							<div className="absolute left-3 top-[9px] tablet:left-[15px] tablet:top-[12px]">
								{authProvider.icon}
							</div>
							<p className=" text-[15px] font-500 text-blue-700">
								{t("fs_continue_with", {
									provider: authProvider.name,
								})}
							</p>
						</button>
					))}
				</div>

				<div className="flex items-center gap-2">
					<div className="h-[1px] flex-1 bg-color-separator" />
					<span className="select-none text-[13px] font-400 text-blue-700">
						{t("lbl_or")}
					</span>
					<div className="h-[1px] flex-1 bg-color-separator" />
				</div>

				<BorderInput
					name="email"
					className=" !h-11 !bg-color-background tablet:!h-[50px]"
					placeholder={t("lbl_email")}
					type="email"
					value={email}
					disabled={requested || notCurrent}
					onChange={function (event) {
						setEmail(event.currentTarget.value)
					}}
					onBlur={emailValidator.validate}
				/>

				<div className="relative flex flex-col items-stretch overflow-visible">
					<div className="relative">
						<BorderInput
							name="password"
							placeholder={t("lbl_password")}
							type={passwordHidden ? "password" : "text"}
							value={password}
							disabled={requested || notCurrent}
							className="peer !h-11 w-full !bg-color-background pr-12 tablet:!h-[50px]"
							onChange={(event) =>
								setPassword(event.currentTarget.value)
							}
						/>

						<PasswordCheckPopup password={password} />

						<button
							type="button"
							disabled={
								passwordHidden ||
								notCurrent ||
								password.length === 0
							}
							onClick={() => setPasswordHidden(true)}
							className={clsx(
								"absolute right-2 top-1/2 -translate-y-1/2 p-2",
								"transition-opacity duration-150",
								passwordHidden &&
									"pointer-events-none opacity-0",
								password.length === 0 &&
									"pointer-events-none opacity-0",
							)}>
							<img
								src={assetUrl(
									"/general/eye-icon-closed.svg",
								)}
								alt="show password"
							/>
						</button>

						<button
							type="button"
							disabled={
								!passwordHidden ||
								notCurrent ||
								password.length === 0
							}
							onClick={() => setPasswordHidden(false)}
							className={clsx(
								"absolute right-2 top-1/2 -translate-y-1/2 p-2",
								"transition-opacity duration-150",
								!passwordHidden &&
									"pointer-events-none opacity-0",
								password.length === 0 &&
									"pointer-events-none opacity-0",
							)}>
							<img
								src={assetUrl("/general/eye-icon.svg")}
								alt="hide password"
							/>
						</button>
					</div>
				</div>

				<div className="relative flex flex-col items-stretch overflow-visible">
					<div className="relative">
						<BorderInput
							name="password"
							placeholder={t("txt_confirm_password")}
							type={confirmHidden ? "password" : "text"}
							className="peer !h-11 w-full !bg-color-background pr-12 tablet:!h-[50px]"
							value={confirm}
							disabled={requested || notCurrent}
							onChange={(event) =>
								setConfirm(event.currentTarget.value)
							}
						/>

						<button
							type="button"
							disabled={confirmHidden || notCurrent}
							onClick={() => setConfirmHidden(true)}
							className={clsx(
								"absolute right-2 top-1/2 -translate-y-1/2 p-2",
								"transition-opacity duration-150",
								confirmHidden &&
									"pointer-events-none opacity-0",
								confirm.length === 0 &&
									"pointer-events-none opacity-0",
							)}>
							<img
								src={assetUrl(
									"/general/eye-icon-closed.svg",
								)}
								alt="hide password"
							/>
						</button>

						<button
							type="button"
							disabled={!confirmHidden || notCurrent}
							onClick={() => setConfirmHidden(false)}
							className={clsx(
								"absolute right-2 top-1/2 -translate-y-1/2 p-2",
								"transition-opacity duration-150",
								!confirmHidden &&
									"pointer-events-none opacity-0",
								confirm.length === 0 &&
									"pointer-events-none opacity-0",
							)}>
							<img
								src={assetUrl("/general/eye-icon.svg")}
								alt="show password"
							/>
						</button>
					</div>
				</div>

				<Button
					type="submit"
					className="w-full"
					text="lbl_next"
					loading={requested}
					disabled={
						requested ||
						notCurrent ||
						notSamePassword ||
						password.trim().length === 0 ||
						email.trim().length === 0
					}
					primary
				/>
				<span className="max-w-full self-center pb-4 text-center text-[12px] tablet:hidden tablet:pb-0">
					<span>{t("txt_by_signing_up_you_accept")}</span>
					<a
						href="/privacy-policy"
						className="mx-1 text-[#007BFF]">
						{t("txt_terms_of_use")}
					</a>
					<span>{t("lbl_and")}</span>
					<a
						href="/privacy-policy"
						className="mx-1 text-[#007BFF]">
						{t("txt_privacy_policy")}
					</a>
				</span>
			</div>

			<div
				className={clsx(
					"fixed bottom-0 left-0 z-[41] w-full bg-color-cell tablet:relative tablet:z-[30] tablet:mt-2 tablet:bg-[transparent]",
					"border-t border-color-separator tablet:mt-[16px] tablet:border-[transparent]",
					"flex justify-center",
				)}>
				<button
					type="button"
					className="h-[60px] text-[15px] tablet:h-auto"
					onClick={() => {
						setPage("signin")
						setMetaTitle("txt_login_title")
					}}>
					{t("txt_already_have_account")}
					<b className="ml-1 font-700 text-primary-500">
						{t("lbl_signin")}
					</b>
				</button>
			</div>
		</form>
	)
}

export function VerifySection(props: SignUpSectionProps) {
	const {
		page,
		email,
		setPage,
		newPassword: password,
		redirectUrl,
		// setQuiz,
		requested,
		setRequested,
	} = props

	const { t } = useTranslation()
	const logAdjust = useAdjustLogger()
	const router = useRouter()

	const [code, setCode] = useState("")
	const [hasError, setHasError] = useState(false)
	const inputRefs = useRef<Array<HTMLInputElement | null>>(
		Array(6).fill(null),
	)

	const notCurrent = page !== "verify"

	const { notify } = useContext(NotificationContext)
	const { createUserWithEmailAndPassword } =
		useAuthUpdater()

	const [timer, setTimer] = useState(60)

	useEffect(() => {
		if (timer === 0 || notCurrent) return

		const intervalId = setInterval(() => {
			setTimer((prevTimer) => prevTimer - 1)
		}, 1000)

		return () => clearInterval(intervalId)
	}, [notCurrent, timer])

	const minutes = Math.floor(timer / 60)
	const seconds = timer % 60
	const timerDisplay = `${minutes
		.toString()
		.padStart(2, "0")}:${seconds
		.toString()
		.padStart(2, "0")}`

	async function handleResend() {
		setTimer(60)

		if (email.trim().length === 0) return

		try {
			await axios
				.post<VerifyEmailRes>("/api/v1/auth/verify/email", {
					email,
				})
				.then((res) => res.data)
		} catch (error) {
			console.error(error)
			notify(
				<ErrorMessage>
					{t("txt_something_wrong")}
				</ErrorMessage>,
			)
		}
	}

	const errorSchema = z.object({
		code: z.string(),
	})

	async function handleSignUp(submitCode: string) {
		if (
			email.trim().length === 0 ||
			password.trim().length === 0 ||
			requested
		)
			return

		setRequested(true)

		try {
			const canContinue = await axios
				.post<boolean>(
					"/api/v1/auth/verify/email/submit-code",
					{
						email,
						code: submitCode,
					},
				)
				.then((res) => res.data)

			if (canContinue) {
				await createUserWithEmailAndPassword(
					email,
					password,
				)

				const hashEmail = (email: string) => {
					return crypto
						.createHash("sha256")
						.update(email)
						.digest("hex")
				}

				const hashedEmail = hashEmail(email)

				logAdjust?.logEventWithPayload("w_did_sign_up", {
					from:
						typeof redirectUrl === "string"
							? redirectUrl
							: "home",
					email: hashedEmail,
				})

				// if (userInfo.details.promptOnboarding) {
				// 	setPage("username")
				// 	setRequested(false)
				// 	return
				// }
				// } else {
				// 	const quizParams = {
				// 		uid: userInfo.userId,
				// 	}
				// 	const quiz = await axios
				// 		.post<Quiz | null>("/api/quiz", quizParams)
				// 		.then((res) => res.data)
				// 	if (quiz) {
				// 		setQuiz(quiz)
				// 		setPage("quiz")
				// 		setRequested(false)
				// 		return
				// 	}
				// }
				let url = "/"

				if (typeof redirectUrl === "string") {
					url = redirectUrl
				}

				await router.push(url)
				return
			} else {
				setHasError(true)
				setRequested(false)
				return
			}
		} catch (error) {
			console.error(error)
			try {
				const parsedError = errorSchema.parse(error)
				switch (parsedError.code) {
					case "auth/email-already-in-use":
						notify(
							<ErrorMessage>
								{t("txt_email_already_in_use")}
							</ErrorMessage>,
						)
						break
					case "auth/invalid-email":
						notify(
							<ErrorMessage>
								{t("txt_email_not_valid")}
							</ErrorMessage>,
						)
						break
					case "auth/operation-not-allowed":
						notify(
							<ErrorMessage>
								{t("txt_operation_not_allowed")}
							</ErrorMessage>,
						)
						break
					case "auth/weak-password":
						notify(
							<ErrorMessage>
								{t("txt_password_too_weak")}
							</ErrorMessage>,
						)
						break
					default:
						notify(
							<ErrorMessage>
								{t("txt_couldnt_create_account")}
							</ErrorMessage>,
						)
						break
				}
			} catch (validationError) {
				notify(
					<ErrorMessage>
						{t("txt_couldnt_create_account")}
					</ErrorMessage>,
				)
			}
		}

		setRequested(false)
	}

	const handleChange = (index: number, value: string) => {
		const sanitizedValue = value.replace(/\D/g, "")

		const characters = sanitizedValue.split("")

		characters.forEach((character, i) => {
			const newIndex = index + i
			if (newIndex < inputRefs.current.length) {
				setCode((prevCode) => {
					const newCode = prevCode.split("")
					newCode[newIndex] = character
					return newCode.join("")
				})
			}
		})

		const lastFilledIndex = index + characters.length - 1
		if (lastFilledIndex >= inputRefs.current.length) {
			inputRefs.current[
				inputRefs.current.length - 1
			]?.focus()
		} else {
			inputRefs.current[lastFilledIndex + 1]?.focus()
		}
	}

	const handleKeyDown = (
		index: number,
		e: React.KeyboardEvent<HTMLInputElement>,
	) => {
		if (e.key === "ArrowLeft") {
			e.preventDefault()
			if (index > 0) {
				inputRefs.current[index - 1]?.focus()
			}
		} else if (e.key === "ArrowRight") {
			e.preventDefault()
			if (index < inputRefs.current.length - 1) {
				inputRefs.current[index + 1]?.focus()
			}
		} else if (
			e.key === "Backspace" &&
			index >= 0 &&
			inputRefs.current[index]?.selectionStart === 0
		) {
			e.preventDefault()
			setCode((prevCode) => {
				const newCode = prevCode.split("")
				newCode[index - 1] = ""
				const updatedCode = newCode.join("")

				inputRefs.current[index - 1]?.focus()

				return updatedCode
			})
		} else if (
			e.key === "Backspace" &&
			index >= 0 &&
			inputRefs.current[index]?.selectionStart === 1
		) {
			e.preventDefault()
			setCode((prevCode) => {
				const newCode = prevCode.split("")
				newCode[index] = ""
				const updatedCode = newCode.join("")

				inputRefs.current[index - 1]?.focus()

				return updatedCode
			})
		}
	}

	return (
		<form
			className={clsx(
				"absolute right-0 top-0 tablet:right-[calc(50%-185px)]",
				"h-full w-full tablet:w-[375px]",
				"flex items-center justify-center",
				"transition-all duration-500",
				page === "verify"
					? ["opacity-100 delay-200", "translate-y-0"]
					: [
							"pointer-events-none opacity-0",
							sectionOrder[page] > sectionOrder.verify
								? "translate-y-24"
								: "-translate-y-24",
					  ],
			)}
			onSubmit={(e) => {
				e.preventDefault()
				handleSignUp(code)
			}}>
			<div className="flex w-full flex-col items-stretch gap-3 px-4 tablet:relative tablet:w-[375px] tablet:px-0">
				<h3 className="text-center text-[20px] font-700 text-blue-800">
					{t("txt_confirm_your_email")}
				</h3>
				<div className="mb-4 text-center text-[18px] font-500 text-blue-600">
					<p>{t("txt_enter_verification_code")}</p>
					<p>{email}</p>
				</div>
				<button
					type="button"
					className={clsx(
						"absolute left-2 top-2 tablet:-top-16 tablet:left-0",
						"bg-blue-200 hover:bg-blue-100",
						"transition-colors duration-200",
						"h-8 w-8 rounded-full px-3",
					)}
					onClick={() => setPage("signup")}>
					<svg
						width="6"
						height="12"
						viewBox="0 0 6 12"
						fill="none"
						xmlns="http://www.w3.org/2000/svg">
						<path
							d="M5.12969 10.2594L0.871094 6.00078L5.12969 1.74219"
							stroke="var(--color-blue-900)"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</button>

				<div className="flex flex-col gap-1">
					<div className="flex flex-row items-center justify-center gap-[9px]">
						{[...Array(6)].map((_, index) => (
							<input
								key={index}
								ref={(ref) =>
									(inputRefs.current[index] = ref)
								}
								type="text"
								pattern="[0-9]*"
								value={code[index] || ""}
								onChange={(e) =>
									handleChange(index, e.target.value)
								}
								onKeyDown={(e) => handleKeyDown(index, e)}
								className={clsx(
									"border-[1.5px]",
									"bg-color-cell outline-none",
									"rounded-[8px]",
									"h-[50px] w-[50px] placeholder-color-placeholder",
									"no-autofill text-center transition-all duration-200",
									hasError && code.length === 6
										? "border-color-error"
										: "border-color-separator focus:border-blue-500",
								)}
							/>
						))}
					</div>
					<p
						className={clsx(
							" px-4 text-[14px] font-500 text-color-error transition-opacity",
							(!hasError || code.length !== 6) &&
								"pointer-events-none select-none opacity-0",
						)}>
						{t("txt_code_doesnt_exist")}
					</p>
				</div>

				<Button
					type="submit"
					className="atablet:mt-[150px] w-full"
					text="lbl_next"
					loading={requested}
					disabled={
						requested || notCurrent || code.length !== 6
					}
					primary
				/>
				<p className="text-center text-[15px] font-400 text-blue-800">
					{t("txt_didnt_receive_email")}
				</p>
				<div className="flex flex-col">
					<p className="text-center text-[16px] font-500 text-blue-600">
						{timerDisplay}
					</p>
					<button
						onClick={(e) => {
							e.preventDefault()
							handleResend()
						}}
						className={clsx(
							"text-center text-[16px] font-500",
							timer === 0
								? "text-primary-500"
								: "text-primary-500/30 pointer-events-none cursor-not-allowed",
						)}>
						{t("txt_resend_code")}
					</button>
				</div>
			</div>
		</form>
	)
}

interface PasswordProperties {
	upperLowerCase: boolean
	symbols: boolean
	numbers: boolean
	enoughCharacters: boolean
}

const MIN_PASSWORD_CHARACTERS = 6
function checkPasswordProperties(
	password: string,
): PasswordProperties {
	const hasUpperCase = password.match(/[A-Z]/) !== null
	const hasLowerCase = password.match(/[a-z]/) !== null
	const symbols =
		password.match(
			/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
		) !== null
	const numbers = password.match(/[0-9]/) !== null
	const enoughCharacters =
		password.length >= MIN_PASSWORD_CHARACTERS

	return {
		upperLowerCase: hasUpperCase && hasLowerCase,
		symbols,
		numbers,
		enoughCharacters,
	}
}

function getPasswordLevelFromProperties(
	properties: PasswordProperties,
): number {
	const {
		upperLowerCase,
		symbols,
		numbers,
		enoughCharacters,
	} = properties
	const requirements = [
		upperLowerCase,
		symbols,
		numbers,
		enoughCharacters,
	]
	const level = requirements.filter(
		(requirement) => requirement,
	).length

	return level
}

interface PasswordCheckPopupProps {
	password: string
}

const levelTexts = [
	"txt_must_have_characters",
	"txt_weak_password",
	"txt_average_password",
	"txt_good_password",
	"txt_strong_password",
]

const PROGRESS_COUNT = 4
export function PasswordCheckPopup(
	props: PasswordCheckPopupProps,
) {
	const { password } = props
	const passwordProperties =
		checkPasswordProperties(password)
	const level = getPasswordLevelFromProperties(
		passwordProperties,
	)

	const { t } = useTranslation()

	const progressCount = new Array(PROGRESS_COUNT).fill(null)

	return (
		<div
			id="password-check"
			className={clsx(
				"peer-focus:pointer-events-auto peer-focus:opacity-100",
				"peer-focus:translate-y-4",
				"peer-focus:desktop:-translate-x-full peer-focus:desktop:-translate-y-1/2",
				"pointer-events-none opacity-0",
				"transition-all duration-300 ease-in-out",
				"absolute left-0 top-full z-40 w-[300px]",
				"desktop:-left-6 desktop:top-1/2",
				"flex flex-col items-stretch gap-4",
				"rounded-[12px] bg-color-cell p-[22px]",
				"[box-shadow:0px_0px_30px_rgba(0,0,0,0.13)]",
				"translate-y-8",
				"desktop:-translate-x-[110%] desktop:-translate-y-1/2",
				"pointer-events-none",
			)}>
			<div className="absolute -right-2 top-1/2 hidden h-6 w-6 -translate-y-1/2 rotate-45 rounded-md bg-color-cell desktop:block" />
			<div className="relative h-[27px]">
				{levelTexts.map((text, index) => (
					<span
						key={index}
						className={clsx(
							"text-[18px] font-600",
							"absolute left-0 top-0",
							index !== level
								? [
										"opacity-0",
										index > level
											? "translate-y-6"
											: "-translate-y-6",
								  ]
								: [
										"opacity-100 delay-100",
										"translate-y-0",
								  ],
							"transition-all duration-200 ease-in-out",
						)}>
						{t(text)}
					</span>
				))}
			</div>
			<div className="flex gap-1">
				{progressCount.map((_, index) => (
					<PasswordCheckProgress
						key={index}
						index={index}
						level={level}
					/>
				))}
			</div>
			<PasswordRecommendations
				properties={passwordProperties}
			/>
		</div>
	)
}

interface PasswordRecommendationsProps {
	properties: PasswordProperties
}

function PasswordRecommendations(
	props: PasswordRecommendationsProps,
) {
	const { properties } = props
	const { t } = useTranslation()

	return (
		<div className="flex flex-col gap-3">
			<span>{t("txt_its_better_to_have")}</span>
			<div className="flex flex-col gap-2 pl-2">
				<PasswordRecommendationTick
					checked={properties.upperLowerCase}>
					{t("txt_upper_and_lower_case")}
				</PasswordRecommendationTick>
				<PasswordRecommendationTick
					checked={properties.symbols}>
					{t("txt_a_symbol")}
				</PasswordRecommendationTick>
				<PasswordRecommendationTick
					checked={properties.numbers}>
					{t("txt_numbers")}
				</PasswordRecommendationTick>
			</div>
		</div>
	)
}

interface PasswordRecommendationTickProps {
	checked: boolean
	children: string
}

function PasswordRecommendationTick(
	props: PasswordRecommendationTickProps,
) {
	const { checked, children } = props

	return (
		<div className="relative pl-4">
			<img
				src={assetUrl("/general/green-tick.svg")}
				alt="checked"
				className={clsx(
					"absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2",
					checked ? "opacity-100 delay-100" : "opacity-0",
					"transition-opacity duration-200 ease-in-out",
					"h-4 w-4",
				)}
			/>
			<span
				className={clsx(
					"absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2",
					checked ? "opacity-0" : "opacity-100 delay-100",
					"transition-opacity duration-200 ease-in-out",
					"h-[6px] w-[6px] rotate-45 bg-blue-100",
				)}
			/>
			<span>{children}</span>
		</div>
	)
}

interface PasswordCheckProgressProps {
	index: number
	level: number
}

function PasswordCheckProgress(
	props: PasswordCheckProgressProps,
) {
	const { index, level } = props
	const colors = [
		"bg-[transparent]",
		"bg-[#F8665B]",
		"bg-[#F3A23A]",
		"bg-[#47A8F8]",
		"bg-[#09C185]",
	]

	return (
		<div
			className={clsx(
				"h-1 flex-1 overflow-hidden rounded-full bg-color-separator",
			)}>
			<div
				className={clsx(
					index < level
						? ["w-full", colors[level]]
						: ["w-0"],
					"h-full",
					"transition-all duration-300 ease-in-out",
				)}
			/>
		</div>
	)
}

export const getServerSideProps = wrapSSRAuth(
	async (context, userInfo): Promise<LoginProps> => {
		const { redirect_url, type, expired } = context.query

		let page: LoginPage = "signin"

		try {
			if (type) {
				const parsedType = loginPageSchema.safeParse(type)
				if (
					parsedType.success &&
					parsedType.data !== "verify" &&
					parsedType.data !== "quiz" &&
					parsedType.data !== "success" &&
					parsedType.data !== "plan" // To be removed
				) {
					if (
						parsedType.data === "username" &&
						(!userInfo ||
							!userInfo.details.promptOnboarding)
					) {
						page = "signin"
					} else {
						page = parsedType.data
					}
				}
			}
		} catch (error) {
			console.error("Invalid LoginPage type:", error)
		}

		if (userInfo && userInfo.details.promptOnboarding) {
			page = "username"
		}

		if (
			userInfo &&
			!userInfo.details.promptOnboarding
			// page !== "plan"
		) {
			throw new SSRTemporaryRedirect("/")
		}

		const isExpired = expired === "true"

		const props: LoginProps = {
			redirect_url: redirect_url ?? null,
			type: page,
			expired: isExpired,
		}

		return props
	},
	[],
	{ skipOnboarding: true },
)
